import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils/utils';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './carousel';

export const TourCarousel = ({
  children,
  arrowsAtBottom,
  className,
  ...rest
}: React.HTMLProps<HTMLDivElement> & { arrowsAtBottom?: boolean }): React.ReactElement => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    api.on('reInit', onSelect);
    onSelect();
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  const scrollTo = (index: number) => {
    api?.scrollTo(index);
  };

  return (
    <div
      className={cn(arrowsAtBottom ? 'w-full h-full flex flex-col' : 'w-full', className)}
      dir='rtl'
      {...rest}
    >
      <Carousel
        opts={{
          align: 'start',
          loop: false,
          direction: 'rtl',
        }}
        setApi={setApi}
        className={
          arrowsAtBottom ? 'w-full flex flex-col relative h-full' : 'w-full flex flex-col relative'
        }
      >
        {arrowsAtBottom ? (
          <>
            <div className='flex-1 min-h-0 overflow-hidden'>
              <CarouselContent className='h-full'>
                {children &&
                  React.Children.map(children, (child, index) => (
                    <CarouselItem data-testid='carousel-slide' className='p-0' key={index}>
                      <div className='p-1'>{child}</div>
                    </CarouselItem>
                  ))}
              </CarouselContent>
            </div>
            <div className='flex items-center justify-between p-5 pt-6 mt-auto shrink-0'>
              <div className='w-14'>
                {selectedIndex !== 0 && (
                  <CarouselPrevious data-testid='carousel-prev-btn' className='relative translate-y-0 translate-x-0 top-auto left-auto rounded-md w-14 h-14' />
                )}
              </div>
              <div className='flex items-center gap-2'>
                {[...Array(React.Children.count(children))].map((_, index) => (
                  <span
                    key={index}
                    onClick={() => scrollTo(index)}
                    className={`w-3 h-3 cursor-pointer border border-pics rounded-full${
                      index === selectedIndex ? ' bg-pics' : ''
                    }`}
                  />
                ))}
              </div>
              <div className='w-14'>
                {selectedIndex !== React.Children.count(children) - 1 && (
                  <CarouselNext data-testid='carousel-next-btn' className='relative translate-y-0 top-auto right-auto rounded-md w-14 h-14' />
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <CarouselContent>
              {children &&
                React.Children.map(children, (child, index) => (
                  <CarouselItem data-testid='carousel-slide' className='p-0' key={index}>
                    <div className='p-1'>{child}</div>
                  </CarouselItem>
                ))}
            </CarouselContent>
            <div className='absolute inset-x-0 bottom-0 flex items-center justify-between p-5'>
              <div className='w-14'>
                {selectedIndex !== 0 && (
                  <CarouselPrevious data-testid='carousel-prev-btn' className='relative translate-y-0 translate-x-0 top-auto left-auto rounded-md w-14 h-14' />
                )}
              </div>
              <div className='flex items-center gap-2'>
                {[...Array(React.Children.count(children))].map((_, index) => (
                  <span
                    key={index}
                    onClick={() => scrollTo(index)}
                    className={`w-3 h-3 cursor-pointer border border-pics rounded-full${
                      index === selectedIndex ? ' bg-pics' : ''
                    }`}
                  />
                ))}
              </div>
              <div className='w-14'>
                {selectedIndex !== React.Children.count(children) - 1 && (
                  <CarouselNext data-testid='carousel-next-btn' className='relative translate-y-0 top-auto right-auto rounded-md w-14 h-14' />
                )}
              </div>
            </div>
          </>
        )}
      </Carousel>
    </div>
  );
};
